import React, { useMemo } from "react";
import { useIntl } from 'react-intl';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import { CombinatorSectionItem } from "../../../ContentfulTypes";
import { Contentful, ContentType } from "../../../types";
import extractArrayData from "../../../utils/extractArrayData";
import isKiosk from '../../../utils/isKiosk';
import RichText from "../../UI/RichText";

import {
  HeadingLink,
  BodyLink,
  Container,
  InfoContainer,
  Logo,
  LowerPrice,
  RedeemPoints,
  Section,
  LogoContainer,
} from "./styles";

interface Props {
  data: Array<CombinatorSectionItem>
}

const headingOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <RedeemPoints data-testid="heading">{children}</RedeemPoints>,
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      <HeadingLink
        route={node?.data?.target?.ctaTarget}
        url={node?.data?.target?.ctaUrl}
        linkText={node?.data?.target?.ctaLabel?.ctaLabel}
        linkColor={node?.data?.target?.ctaColor}
        linkSize="large"
        ariaLabel={node?.data?.target?.ctaLabel?.ctaLabel}
        locale={locale}
        data-testid="embedded-link-heading"
      />
    ),
  },
};

const headingOptionsKiosk = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <RedeemPoints data-testid="heading">{children}</RedeemPoints>,
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      node?.data?.target?.ctaHideOption?.includes('KIOSK') ? (
        null
      ) : (
        <HeadingLink
          route={node?.data?.target?.ctaTarget}
          url={node?.data?.target?.ctaUrl}
          linkText={node?.data?.target?.ctaLabel?.ctaLabel}
          linkColor={node?.data?.target?.ctaColor}
          linkSize="large"
          ariaLabel={node?.data?.target?.ctaLabel?.ctaLabel}
          locale={locale}
          data-testid="embedded-link-heading"
        />
      )
    ),
  },
};

const contentBodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <LowerPrice data-testid="contentBody">{children}</LowerPrice>,
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      <BodyLink
        route={node?.data?.target?.ctaTarget}
        url={node?.data?.target?.ctaUrl}
        linkText={node?.data?.target?.ctaLabel?.ctaLabel}
        linkColor={node?.data?.target?.ctaColor}
        linkSize="large"
        ariaLabel={node?.data?.target?.ctaLabel?.ctaLabel}
        locale={locale}
        data-testid="embedded-link-content"
      />
    ),
  },
};

const contentBodyOptionsKiosk = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <LowerPrice data-testid="contentBody">{children}</LowerPrice>,
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => (
      node?.data?.target?.ctaHideOption?.includes('KIOSK') ? (
        null
      ) : (
        <BodyLink
          route={node?.data?.target?.ctaTarget}
          url={node?.data?.target?.ctaUrl}
          linkText={node?.data?.target?.ctaLabel?.ctaLabel}
          linkColor={node?.data?.target?.ctaColor}
          linkSize="large"
          ariaLabel={node?.data?.target?.ctaLabel?.ctaLabel}
          locale={locale}
          data-testid="embedded-link-content"
        />
      )
    ),
  },
};

const PointsInfoSection = ({ data }:Props) => {
  const isKioskMode = isKiosk();
  const heading = useMemo(() => extractArrayData(
    data,
    Contentful.TypeName,
    ContentType.ContentfulTextSection,
  ), [data]);

  const logoData = useMemo(() => extractArrayData(
    data,
    Contentful.TypeName,
    ContentType.ContentfulImageSection,
  ), [data]);

  return (
    <Section data-testid="points-info-section">
      <Container>
        <InfoContainer>
          {heading?.heading && (
          <RichText
            document={heading?.heading}
            options={isKioskMode ? headingOptionsKiosk : headingOptions}
          />
          )}
          {heading?.contentBody && (
          <RichText
            document={heading?.contentBody}
            options={isKioskMode ? contentBodyOptionsKiosk : contentBodyOptions}
          />
          )}
          {logoData?.foregroundImageDesktop && (
          <LogoContainer>
            <Logo image={logoData?.foregroundImageDesktop} data-testid="logo" />
          </LogoContainer>
          )}
        </InfoContainer>
      </Container>
    </Section>
  );
};

export default PointsInfoSection;
